import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Stack,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SecurityIcon from "@mui/icons-material/Security";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { API_BASE } from "../config";
import { useNavigate } from "react-router-dom";

/* =====================================================
   🎨 KURUMSAL RENKLER
===================================================== */
const COLORS = {
  primary: "#0d6efd", // mavi
  secondary: "#fd7e14", // turuncu
};

/* =====================================================
   🌍 DİL METİNLERİ
===================================================== */
const T = {
  tr: {
    title: "Muhasebe • Sigorta • Finans",
    loading: "Yükleniyor…",
    empty: "Henüz tanımlanmış checklist yok.",
    done: "Tamamlandı",
    pending: "Bekliyor",
    muhasebe: "Muhasebe & Vergi",
    sigorta: "Sigorta İşlemleri",
    finans: "Finans & Ödemeler",
    goDocs: "Belgeleri Gör / Belge Yükle",
    sigortaInfo: "Sigorta poliçeleri ve belgeler buradan yönetilecek.",
    finansInfo: "Ödeme, banka ve finans süreçleri buradan yönetilecek.",
  },
  de: {
    title: "Buchhaltung • Versicherung • Finanzen",
    loading: "Wird geladen…",
    empty: "Keine Checkliste definiert.",
    done: "Erledigt",
    pending: "Offen",
    muhasebe: "Buchhaltung & Steuern",
    sigorta: "Versicherungen",
    finans: "Finanzen & Zahlungen",
    goDocs: "Dokumente anzeigen / hochladen",
    sigortaInfo: "Versicherungspolicen und Dokumente werden hier verwaltet.",
    finansInfo: "Finanz- und Zahlungsprozesse werden hier verwaltet.",
  },
  en: {
    title: "Accounting • Insurance • Finance",
    loading: "Loading…",
    empty: "No checklist defined yet.",
    done: "Completed",
    pending: "Pending",
    muhasebe: "Accounting & Tax",
    sigorta: "Insurance",
    finans: "Finance & Payments",
    goDocs: "View / Upload Documents",
    sigortaInfo: "Insurance policies and documents will be managed here.",
    finansInfo: "Finance and payment processes will be managed here.",
  },
};

export default function MuhasebeDashboard() {
  const navigate = useNavigate();
  const lang = localStorage.getItem("lang") || "tr";
  const t = T[lang];

  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    fetch(`${API_BASE}/accounting/checklist`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setChecklist(d.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const renderChecklist = () => {
    if (loading) {
      return (
        <Box py={3} textAlign="center">
          <CircularProgress />
          <Typography mt={2}>{t.loading}</Typography>
        </Box>
      );
    }

    if (!checklist.length) {
      return <Typography color="text.secondary">{t.empty}</Typography>;
    }

    return (
      <Stack spacing={1.5}>
        {checklist.map((item, i) => (
          <Box
            key={i}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              p: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography>{item.title}</Typography>

            <Chip
              label={item.done ? t.done : t.pending}
              sx={{
                backgroundColor: item.done ? "#e7f5ee" : "#fff3cd",
                color: item.done ? "#13795b" : "#b78103",
                fontWeight: 500,
              }}
            />
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <Box p={{ xs: 2, md: 4 }}>
      <Typography
        variant="h4"
        fontWeight={700}
        mb={3}
        sx={{ color: COLORS.primary }}
      >
        🧾 {t.title}
      </Typography>

      {/* =========================
          🧾 MUHASEBE
         ========================= */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <ReceiptLongIcon sx={{ color: COLORS.primary, mr: 1 }} />
          <Typography fontWeight={600}>{t.muhasebe}</Typography>
        </AccordionSummary>

        <AccordionDetails>
          {renderChecklist()}

          <Box mt={3} textAlign="right">
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              sx={{
                backgroundColor: COLORS.secondary,
                "&:hover": { backgroundColor: "#e8590c" },
              }}
              onClick={() => navigate("/muhasebe/belgeler")}
            >
              {t.goDocs}
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* =========================
          🛡️ SİGORTA
         ========================= */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SecurityIcon sx={{ color: COLORS.secondary, mr: 1 }} />
          <Typography fontWeight={600}>{t.sigorta}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography color="text.secondary">{t.sigortaInfo}</Typography>
        </AccordionDetails>
      </Accordion>

      {/* =========================
          💰 FİNANS
         ========================= */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <AccountBalanceIcon sx={{ color: COLORS.primary, mr: 1 }} />
          <Typography fontWeight={600}>{t.finans}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography color="text.secondary">{t.finansInfo}</Typography>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
